import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, Sparkles } from "lucide-react";
import { useRef } from "react";
import { scrollToId } from "../lib/smooth";

const ease = [0.16, 1, 0.3, 1];
const lines = [
  { t: "We turn", c: "" },
  { t: "filament", c: "rainbow-text" },
  { t: "into fun.", c: "font-serif-i font-normal" },
];

const Badge = () => (
  <div className="absolute -left-6 -bottom-8 sm:-left-10 w-28 h-28 sm:w-36 sm:h-36 z-20" data-testid="hero-rotating-badge">
    <svg viewBox="0 0 200 200" className="w-full h-full spin-slow">
      <defs><path id="c" d="M100,100 m-72,0 a72,72 0 1,1 144,0 a72,72 0 1,1 -144,0" /></defs>
      <circle cx="100" cy="100" r="98" fill="#FFD23F" stroke="#14101F" strokeWidth="4" />
      <text className="font-mono-x" fontSize="17" fontWeight="700" letterSpacing="3" fill="#14101F">
        <textPath href="#c">CUSTOM PRINTS • MADE WITH CREATIVITY •</textPath>
      </text>
    </svg>
    <Sparkles className="absolute inset-0 m-auto w-8 h-8 text-[var(--pink)]" />
  </div>
);

const TiltStack = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [12, -12]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  return (
    <div onMouseMove={onMove} onMouseLeave={reset} className="relative [perspective:1200px]" data-testid="hero-3d-stack">
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative aspect-[4/5] w-full">
        <div className="absolute inset-0 rounded-[28px] bg-[var(--cyan)] chunk" style={{ transform: "translateZ(-60px) rotate(-6deg)" }} />
        <div className="absolute inset-0 rounded-[28px] bg-[var(--pink)] chunk" style={{ transform: "translateZ(-30px) rotate(4deg)" }} />
        <div className="absolute inset-0 rounded-[28px] overflow-hidden chunk bg-white" style={{ transform: "translateZ(0px)" }}>
          <img src="/images/p-controller-robot.jpg" alt="3D printed robot hand holding a blue game controller" className="w-full h-full object-cover" />
        </div>
        <div className="absolute -right-4 top-8 sm:-right-8 px-4 py-2 rounded-full bg-[var(--lime)] chunk-sm font-display font-extrabold text-sm sm:text-base"
          style={{ transform: "translateZ(70px)" }}>
          Printed layer by layer
        </div>
      </motion.div>
      <Badge />
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative pt-24 sm:pt-36 pb-16 sm:pb-28 px-4 sm:px-8 overflow-hidden layer-lines">
      <div className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-[var(--yellow)] opacity-60 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-32 w-[360px] h-[360px] rounded-full bg-[var(--cyan)] opacity-30 blur-3xl pointer-events-none" />
      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}
            className="font-mono-x text-xs sm:text-sm uppercase tracking-[0.2em] font-bold mb-6" data-testid="hero-eyebrow">
            Welcome to Scratchs3dprintz
          </motion.p>
          <h1 className="font-display font-extrabold tracking-tighter leading-[0.9] text-[15vw] sm:text-7xl lg:text-[7.5rem]" data-testid="hero-title">
            {lines.map((l, i) => (
              <span key={l.t} className="block overflow-hidden pb-[0.08em]">
                <motion.span className={`block ${l.c}`} initial={{ y: "110%" }} animate={{ y: "0%" }}
                  transition={{ duration: 1.1, delay: 0.15 + i * 0.13, ease }}>
                  {l.t}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.7, ease }}
            className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[#3b3550]">
            Welcome to our little corner of the 3D printing world! Fun, colourful and practical creations, from quirky desk accessories and gaming gear to unique toys, personalised gifts and custom designs.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85, ease }}
            className="mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:gap-4">
            <button data-testid="hero-shop-btn" onClick={() => scrollToId("shop")}
              className="press chunk rounded-full bg-[var(--ink)] text-[var(--paper)] px-7 py-4 font-bold text-base sm:text-lg flex items-center justify-center gap-2">
              Shop ready-made <ArrowDownRight className="w-5 h-5" />
            </button>
            <button data-testid="hero-custom-btn" onClick={() => scrollToId("custom")}
              className="press chunk rounded-full bg-[var(--yellow)] px-7 py-4 font-bold text-base sm:text-lg text-center">
              Request a custom print
            </button>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yImg }} initial={{ opacity: 0, scale: 0.92, rotate: -3 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1.2, delay: 0.3, ease }} className="lg:col-span-5 max-w-sm sm:max-w-md mx-auto w-full px-6 sm:px-0">
          <TiltStack />
        </motion.div>
      </div>
    </section>
  );
};
